import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { limitShortfall, type LimitShortfall } from 'fym-shared/src/music24/limitUpsell';
import { fymAPI, useGetPromoQuotaQuery } from '../../../../redux/api/fymAPI';
import { isDemoAccountId } from '../../../accountSelector/demoAccounts';
import { parseM24LimitError } from '../../utils/parseM24LimitError';

interface UseComposerPostQuotaProps {
  accountId: number;
  /** The channels of the post: each one takes one post of this month. */
  channelCount: number;
  /** An edit keeps the unit that the post has, or takes one: the server judges it. */
  isEditing: boolean;
  /** The error of the last write; a 402 of the scheduled posts reads the posts left again. */
  writeError: unknown;
}

export interface ComposerPostQuota {
  /** The posts that the new post needs against the posts left this month; null when it fits. */
  shortfall: LimitShortfall | null;
  /** The last write failed because the month has no post left: the card says it, not an alert. */
  isQuotaError: boolean;
}

/**
 * The scheduled posts of the Composer (design limit-upsell a10). A new post takes one post of this
 * month for each channel (`POST promo/posts`), also for a later time. The check runs before the
 * click with `GET promo/quota`; a 402 `M24_LIMIT_QUOTA` of the server reads the count again, so the
 * same card shows. A draft takes no post.
 */
export function useComposerPostQuota({
  accountId,
  channelCount,
  isEditing,
  writeError,
}: UseComposerPostQuotaProps): ComposerPostQuota {
  const dispatch = useDispatch();
  const { data } = useGetPromoQuotaQuery({ accountId }, { skip: isDemoAccountId(accountId) });
  const refusal = parseM24LimitError(writeError);
  const isQuotaError = refusal !== null && refusal.code === 'M24_LIMIT_QUOTA' && refusal.kind === 'scheduledPost';

  useEffect(() => {
    if (isQuotaError) {
      dispatch(fymAPI.util.invalidateTags(['PromoQuota']));
    }
  }, [dispatch, isQuotaError]);

  const quota = data?.scheduledPost;
  const shortfall = useMemo(() => {
    if (isEditing || quota === undefined || quota.limit === null) {
      return null;
    }
    return limitShortfall('posts', channelCount, Math.max(0, quota.limit - quota.used));
  }, [channelCount, isEditing, quota]);

  return { shortfall, isQuotaError };
}
